import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Hammer, 
  Palette, 
  Briefcase, 
  Calculator, 
  Wrench, 
  Smartphone, 
  Scissors, 
  Utensils, 
  Boxes, 
  Shirt, 
  Dog, 
  Stethoscope, 
  Factory, 
  ArrowRight,
  Zap
} from 'lucide-react';
import { safeStorage } from '../../utils/safeStorage';

interface NicheQuickItem {
  id: string;
  name: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  tab: 'pro_gratis' | 'especialista' | 'empresa';
  planTier: 'gratis' | 'pro' | 'especialista' | 'empresa';
  planLabel: string;
}

const NICHES_SHOWCASE: {
  categoryTitle: string;
  categorySubtitle: string;
  badge: string;
  badgeColor: string;
  items: NicheQuickItem[];
}[] = [
  {
    categoryTitle: 'Autônomos, Prestadores & Fabricação',
    categorySubtitle: 'Para quem calcula Valor Hora, insumos, receitas e emite orçamentos com margem garantida.',
    badge: 'Pro & Grátis',
    badgeColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    items: [
      {
        id: 'marcenaria_pro',
        name: 'Marcenaria & Sob Medida',
        desc: 'Chapas, ferragens, insumos, sobras e mark-up protegido em PDF.',
        icon: Hammer,
        tab: 'pro_gratis',
        planTier: 'pro',
        planLabel: 'Pro'
      },
      {
        id: 'artesanato_food_pro',
        name: 'Confeitaria, Doces & Artesanato',
        desc: 'Fichas técnicas de receitas com rendimento por porção e embalagens.',
        icon: Palette,
        tab: 'pro_gratis',
        planTier: 'pro',
        planLabel: 'Pro'
      },
      {
        id: 'autonomous_pro',
        name: 'Prestadores Individuais & Liberais',
        desc: 'Cálculo de hora técnica, catálogo de serviços e propostas comerciais.',
        icon: Briefcase,
        tab: 'pro_gratis',
        planTier: 'pro',
        planLabel: 'Pro'
      },
      {
        id: 'basic_free',
        name: 'Precificação Pontual (Grátis)',
        desc: 'Até 5 orçamentos por mês permanentemente sem cartão.',
        icon: Calculator,
        tab: 'pro_gratis',
        planTier: 'gratis',
        planLabel: 'Grátis'
      }
    ]
  },
  {
    categoryTitle: 'Serviços Técnicos, Obras & Equipes',
    categorySubtitle: 'Para prestadores que utilizam Ordens de Serviço (OS), agenda de atendentes e recibos PIX.',
    badge: 'Especialista',
    badgeColor: 'bg-orange-50 text-orange-800 border-orange-200',
    items: [
      {
        id: 'cell_phone_repair',
        name: 'Assistência de Celulares & Tablets',
        desc: 'Checklist de bancada, dados do aparelho, laudo fotográfico e assinatura na tela.',
        icon: Smartphone,
        tab: 'especialista',
        planTier: 'especialista',
        planLabel: 'Especialista'
      },
      {
        id: 'technical_service',
        name: 'Assistência Técnica, Refrigeração & Obras',
        desc: 'OS com fotos antes/depois, assinatura do cliente e equipe com segredo comercial.',
        icon: Wrench,
        tab: 'especialista',
        planTier: 'especialista',
        planLabel: 'Especialista'
      },
      {
        id: 'general_corporate',
        name: 'Salão de Beleza, Barbearia & Estética',
        desc: 'Agenda multi-profissionais, comissões automáticas e recibos PIX profissionais.',
        icon: Scissors,
        tab: 'especialista',
        planTier: 'especialista',
        planLabel: 'Especialista'
      }
    ]
  },
  {
    categoryTitle: 'Comércio, Balcão PDV, Clínicas & Indústria',
    categorySubtitle: 'Para empresas que precisam de Frente de Caixa com balança, prontuário, lotes e fiscal SEFAZ.',
    badge: 'Empresa',
    badgeColor: 'bg-blue-50 text-blue-800 border-blue-200',
    items: [
      {
        id: 'food_icecream',
        name: 'Sorveteria, Açaí & Buffet a Quilo',
        desc: 'Frente de Caixa PDV com balança serial (tara), multi-comandas e fechamento cego.',
        icon: Utensils,
        tab: 'empresa',
        planTier: 'empresa',
        planLabel: 'Empresa'
      },
      {
        id: 'retail_bulk',
        name: 'Empório, Granel & Mercadinho',
        desc: 'Venda por grama/quilo, controle de validade de lotes e portal do contador.',
        icon: Boxes,
        tab: 'empresa',
        planTier: 'empresa',
        planLabel: 'Empresa'
      },
      {
        id: 'fashion_retail',
        name: 'Loja de Roupas, Boutique & Varejo',
        desc: 'Leitor de código de barras USB, grade de cores/tamanhos e comissão por vendedor.',
        icon: Shirt,
        tab: 'empresa',
        planTier: 'empresa',
        planLabel: 'Empresa'
      },
      {
        id: 'pet_vet',
        name: 'Veterinária & Pet Shop',
        desc: 'Prontuário SOAP, multi-pets por tutor, vacinas no WhatsApp e cofre de exames.',
        icon: Dog,
        tab: 'empresa',
        planTier: 'empresa',
        planLabel: 'Empresa'
      },
      {
        id: 'medical',
        name: 'Clínica Médica & Consultório',
        desc: 'Prontuário eletrônico legal, agenda por médico e receituários em PDF.',
        icon: Stethoscope,
        tab: 'empresa',
        planTier: 'empresa',
        planLabel: 'Empresa'
      },
      {
        id: 'industrial_pcp',
        name: 'Indústria, Fábrica & PCP',
        desc: 'Estrutura de produto (BOM), chão de fábrica, etiquetas ZPL/RFID e reforma fiscal.',
        icon: Factory,
        tab: 'empresa',
        planTier: 'empresa',
        planLabel: 'Empresa'
      }
    ]
  }
];

export default function NicheGatewaySection() {
  const navigate = useNavigate();

  const handleSelectNiche = (niche: NicheQuickItem) => {
    safeStorage.setItem('rmax_pending_niche_id', niche.id);
    safeStorage.setItem('rmax_pending_niche_name', niche.name);
    safeStorage.setItem('rmax_pending_plan', niche.planTier);
    navigate(`/selecionar-perfil?tab=${niche.tab}&niche=${niche.id}`);
  };

  return (
    <section 
      id="nichos" 
      className="container mx-auto px-6 py-16 md:py-24 border-t border-slate-200/80 scroll-mt-20"
      aria-labelledby="niche-gateway-heading"
    >
      <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-100 border border-slate-300 text-slate-800 text-xs font-bold uppercase tracking-wider mb-4">
          <Zap className="w-3.5 h-3.5 text-emerald-600 fill-current" />
          <span>Configuração Sob Medida</span>
        </div>
        <h2 id="niche-gateway-heading" className="text-2xl md:text-4xl lg:text-5xl font-black text-slate-950 tracking-tight mb-4">
          Para qual ramo você precisa do <span className="text-emerald-600">R-MAX</span>?
        </h2>
        <p className="text-slate-600 text-sm md:text-base leading-relaxed">
          Selecione a sua área de atuação. O sistema ajusta as ferramentas, cálculos e telas automaticamente para a rotina real do seu negócio.
        </p>
      </div>

      <div className="space-y-10 max-w-6xl mx-auto">
        {NICHES_SHOWCASE.map((group, groupIdx) => (
          <div key={groupIdx} className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-6 border-b border-slate-100 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900">{group.categoryTitle}</h3>
                  <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${group.badgeColor}`}>
                    {group.badge}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-slate-600">{group.categorySubtitle}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelectNiche(item)}
                    className="text-left p-4 rounded-2xl border border-slate-200/90 hover:border-emerald-600 hover:ring-2 hover:ring-emerald-500/20 bg-slate-50/50 hover:bg-white transition-all group flex flex-col justify-between cursor-pointer"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-700 group-hover:bg-emerald-600 group-hover:text-white group-hover:border-emerald-600 transition-colors shadow-2xs">
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 group-hover:text-emerald-700">
                          {item.planLabel}
                        </span>
                      </div>
                      <h4 className="text-sm font-bold text-slate-900 mb-1 leading-snug group-hover:text-emerald-950">
                        {item.name}
                      </h4>
                      <p className="text-xs text-slate-600 leading-relaxed font-normal mb-3">
                        {item.desc}
                      </p>
                    </div>

                    <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-xs font-bold text-emerald-700 group-hover:text-emerald-800">
                      <span>Configurar meu ambiente</span>
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 text-center">
        <p className="text-xs text-slate-500 mb-3">
          Não encontrou exatamente o seu ramo? O R-MAX adapta campos e orçamentos para qualquer atividade.
        </p>
        <button
          type="button"
          onClick={() => navigate('/selecionar-perfil')}
          className="inline-flex items-center gap-2 text-xs font-bold text-emerald-700 hover:text-emerald-800 underline cursor-pointer"
        >
          <span>Ver catálogo completo de nichos e planos</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </section>
  );
}
